import { AxiosResponse } from 'axios';
import { QueryClient, UseMutationResult } from 'react-query';
import { _AdminStaffData, _HttpMethods, _Payload, _UserType } from '../constants/staticTypes';
import { addAdminStaffApi, getAdminStaffApi, getAdminStaffById, updateAdminStaffStatusApi } from '../constants/apiEndPoints';
import { getErrorDetails, getErrorMessage } from '../utils/common';
import { AlertVariant } from '../constants/constants';
import HttpServiceHelper from './httpServiceHelper';
import * as Yup from 'yup';

export type _AddAdminStaffFormType = {
    loginUserName: string;
    password: string;
    preferredName: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    hireDate: string;
    staffPermissionRoles: string;
    photoKey: string;
    passwordLessLoginOnly: boolean;
    sendPasswordCreation: boolean;
    sendPasswordCreationTo: 'phone' | 'email';
    userType: _UserType;
    organizations: [];
    id?: string; // to be used when editing staff
    signedUrl?: string;
};

export const initialAddAdminStaffDataValues: _AddAdminStaffFormType = {
    loginUserName: '',
    password: '',
    preferredName: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    hireDate: '',
    staffPermissionRoles: '',
    organizations: [],
    photoKey: '',
    sendPasswordCreation: true,
    passwordLessLoginOnly: true,
    sendPasswordCreationTo: 'email',
    userType: 'adminStaff',
    signedUrl: '',
};

export const getAddAdminStaffApiFormattedData = (formData: _AddAdminStaffFormType) => {
    return {
        ...formData,
        staffPermissionRoles: [formData.staffPermissionRoles],
        organizations: formData?.organizations?.map((item: any) => {
            return {
                organizationId: item.organization.id,
                staffPermissionRoles: [item.permissionGroup.id],
            };
        }),
    };
};

export const addAdminStaff = (
    addAdminOrgPermissionMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _AdminStaffData,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    addAdminOrgPermissionMutation.mutate(
        {
            url: addAdminStaffApi,
            method: _HttpMethods.POST,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export const editAdminStaff = (
    addAdminOrgPermissionMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _AdminStaffData,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    addAdminOrgPermissionMutation.mutate(
        {
            url: addAdminStaffApi,
            method: _HttpMethods.PUT,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export const getAdminStaffList = (queryClient: QueryClient, page: number, limit: number, search?: string) => {
    let url = `${getAdminStaffApi}?limit=${limit}&page=${page}`;
    if (search) {
        url += search;
    }

    return HttpServiceHelper({
        url,
        method: _HttpMethods.GET,
        queryClient,
    });
};

export type _AdminStaffOrganizationForm = {
    organization: {
        id: string;
        organizationName: string;
    };
    permissionGroup: {
        id: string;
        permissionRoleName: string;
    };
};

export const adminStaffOrganizationFormInitialValues: _AdminStaffOrganizationForm = {
    organization: {
        id: '',
        organizationName: '',
    },
    permissionGroup: {
        id: '',
        permissionRoleName: '',
    },
};

export const addAdminStaffValidationSchema = Yup.object({
    loginUserName: Yup.string().required('Username is required'),
    sendPasswordCreation: Yup.boolean(),
    password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
        .matches(/\d/, 'Password must contain at least one number')
        .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character'),
    preferredName: Yup.string().required('Display name is required'),
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last Name is required'),
    email: Yup.string().email('Invalid email format').required('Email is required'),
});

export type _AdminStaffId = {
    id: string;
};

export type _UpdateStaffStatus = {
    id: string;
    newStatus: boolean;
};

export const getAdminStaffDetailByID = (
    getAdminStaffDetailByIDMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _AdminStaffId,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    getAdminStaffDetailByIDMutation.mutate(
        {
            url: getAdminStaffById,
            method: _HttpMethods.POST,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export const deleteAdminStaffDetailByID = (
    getAdminStaffDetailByIDMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _AdminStaffId,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    getAdminStaffDetailByIDMutation.mutate(
        {
            url: addAdminStaffApi,
            method: _HttpMethods.DELETE,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export const updateAdminStaffStatus = (
    updateAdminStaffStatusMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _UpdateStaffStatus,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    updateAdminStaffStatusMutation.mutate(
        {
            url: updateAdminStaffStatusApi,
            method: _HttpMethods.POST,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};
